import { Alert, AlertTitle, Box, Button, Divider, Grid } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { createInvoice } from 'src/shared/services/invoiceService';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { InvoiceRequest } from 'src/modules/invoice/models/invoiceRequest';
import dayjs from 'dayjs';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { InvoiceMissionTypeEnum } from 'src/shared/models/enums/invoiceMissionType.enum';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { Member } from '../models/member';
import { MemberCreateInvoiceArticleForm } from './memberCreateInvoiceArticleForm';

type MemberCreateInvoiceModalForGiftOrPurchaseProps = {
  member?: Member;
  members?: Member[];
  invoiceMissionId: InvoiceMissionTypeEnum;
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export const createInvoiceMissionTypes: SelectableItem[] = [
  {
    label: 'invoice:createInvoice.invoiceMission.gift',
    value: InvoiceMissionTypeEnum.Gift,
  },
  {
    label: 'invoice:createInvoice.invoiceMission.purchase',
    value: InvoiceMissionTypeEnum.Purchase,
  },
  // {
  //   label: 'invoice:createInvoice.invoiceMission.memberfee',
  //   value: InvoiceMissionTypeEnum.MemberFee,
  // },
];

export const MemberCreateInvoiceModalForGiftOrPurchase = (props: MemberCreateInvoiceModalForGiftOrPurchaseProps) => {
  const { member = undefined, members = undefined, invoiceMissionId, modalOpen, onClose, onSave } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [ownerId, setOwnerId] = useState<number>();
  const hasOwnerId = useCallback(() => ownerId !== undefined && ownerId !== 0, [ownerId]);

  const getInvoiceMissionName = () => {
    if (!invoiceMissionId) return '';
    const selectedInvoiceMissionType = createInvoiceMissionTypes?.find((x) => x.value === invoiceMissionId);

    if (selectedInvoiceMissionType) {
      return t(selectedInvoiceMissionType.label);
    }

    return '';
  };

  const amountValidationMessage = t('common:validationMessages.amount');
  const requiredMessage = t('common:validationMessages.required');

  const invoiceRowSchema = yup.object().shape({
    articleNr: yup.string().min(1, requiredMessage).required(requiredMessage),
    amount: yup.number().required(amountValidationMessage).min(1, amountValidationMessage).typeError(amountValidationMessage),
    text: yup.string().required(requiredMessage),
  });

  const CreateInvoiceSchema: Schema<InvoiceRequest> = yup.object().shape({
    customer_id: yup.number().notRequired(),
    customerIds: yup.array().notRequired(),
    invoiceMission_id: yup.mixed<InvoiceMissionTypeEnum>().required(requiredMessage),
    owner_id: yup.number().required(),
    dueDate: yup.string().required(requiredMessage),
    invoiceText: yup.string().optional(),
    billText: yup.string().optional(),
    heading: yup.string().optional(),
    subHeading: yup.string().optional(),
    rows: yup.array().of(invoiceRowSchema).required(),
  });

  const formMethods = useForm<InvoiceRequest>({
    resolver: yupResolver(CreateInvoiceSchema),
    mode: 'all',
    defaultValues: {
      customer_id: 0,
      invoiceMission_id: InvoiceMissionTypeEnum.None,
      owner_id: 0,
      dueDate: DateHelper.getFSDate(dayjs(new Date()).add(1, 'month')),
      invoiceText: '',
      billText: '',
      heading: '',
      subHeading: '',
      rows: [{ articleNr: '0', amount: 0, text: '' }],
    },
  });

  const { handleSubmit, setValue } = formMethods;

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = () => {
    onSave();
  };

  useEffect(() => {
    setValue('invoiceMission_id', invoiceMissionId);
  }, [invoiceMissionId, setValue]);

  const onSubmit = useCallback(
    async (data: InvoiceRequest) => {
      // eslint-disable-next-line no-param-reassign
      if (member?.member_id !== undefined) data.customer_id = member.member_id;
      // eslint-disable-next-line no-param-reassign
      if (members && members.length > 0) data.customerIds = members.map((x) => x.member_id || 0);
      // eslint-disable-next-line no-param-reassign
      if (hasOwnerId() && ownerId !== undefined) data.owner_id = ownerId;
      setLoading(true);
      try {
        const result = await createInvoice(data);
        if (isMounted()) {
          onSave();
          toast.success(<>{t('invoice:createInvoice.success')}</>);
          navigate(`${RoutesEnum.InvoicesDetail}/${result.data}`);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [member?.member_id, members, hasOwnerId, ownerId, isMounted, onSave, t, navigate],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogTitle={t('invoice:createInvoice.modalHeader')}
      dialogSubTitle={getInvoiceMissionName()}
      showDialogActions={false}
      padding={0}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* <Alert icon={false} severity='info' sx={{ m: 0 }}>
                {(member || (members && members?.length > 1)) && (
                  <AlertTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <People fontSize='small' sx={{ mr: 1 }} aria-hidden />
                    {t('groupMessage:selection.recipients')}&nbsp;<strong>({members && members.length ? members.length : 1})</strong>
                  </AlertTitle>
                )}
                {member && <RecipientList members={[member]} />}
                {members && <RecipientList members={members} />}
              </Alert>
              <Divider /> */}
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <InvoiceGroups
                    callback={(ownId) => {
                      setOwnerId(ownId);
                    }}
                    placeholder={t('invoice:createInvoice.selectInvoiceGroup')}
                    emptyLabel={t('invoice:createInvoice.selectInvoiceGroup')}
                    autoSelect
                    ownerId={ownerId}
                  />
                  {!hasOwnerId() && (
                    <Alert severity='info' sx={{ mt: 2 }}>
                      <AlertTitle>{t('invoice:createInvoice.selectOwnerIdWarningTitle')}</AlertTitle>
                      {t('invoice:createInvoice.selectOwnerIdWarningText')}
                    </Alert>
                  )}
                </Box>
                {hasOwnerId() && (
                  <Grid container spacing={2}>
                    <MemberCreateInvoiceArticleForm />
                  </Grid>
                )}
              </Box>

              <Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <Button variant='outlined' sx={{ mr: 1 }} onClick={onHandleClose}>
                    {t('common:button.cancel')}
                  </Button>
                  <LoadingButton loading={loading} type='submit' variant='contained' disableElevation onClick={handleSubmitClick}>
                    {t('common:button.save')}
                  </LoadingButton>
                </Box>
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
    />
  );
};
